import { DOCUMENT, Location } from '@angular/common';
import { DestroyRef, inject, Service } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs';

import { STANDALONE_RUNTIME_CONFIG } from '../shell/runtime/config';
import { TranslationService } from '../shell/translation-service';
import { localeFromUrl, stripLocalePrefix, type SupportedLocale } from './locales';

@Service()
export class StandaloneLocale {
  private readonly router = inject(Router);
  private readonly translations = inject(TranslationService);
  private readonly document = inject(DOCUMENT);
  private readonly location = inject(Location);
  private readonly config = inject(STANDALONE_RUNTIME_CONFIG);
  private readonly destroyRef = inject(DestroyRef);

  initialize(): Promise<void> {
    this.router.events
      .pipe(
        filter((event): event is NavigationEnd => event instanceof NavigationEnd),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((event) => {
        // NavigationEnd excludes cancelled/guard-rejected locale changes.
        const locale = localeFromUrl(event.urlAfterRedirects);
        this.document.documentElement.lang = locale;
        void this.translations
          .setContext(locale, this.config.catalogueRevision)
          .catch((error: unknown) => {
            console.error('[IMA i18n] Unable to activate standalone locale:', error);
          });
      });

    // The root renders before initial navigation ends, so seed its locale now.
    const locale = localeFromUrl(this.location.path());
    this.document.documentElement.lang = locale;

    return this.translations.setContext(locale, this.config.catalogueRevision);
  }

  async switchLocale(locale: SupportedLocale): Promise<boolean> {
    return this.router.navigateByUrl(`/${locale}${stripLocalePrefix(this.router.url)}`);
  }
}
